import { Link, useCanGoBack, useRouter } from '@tanstack/react-router'
import type { LinkProps } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type BackButtonProps = {
  /** Where to navigate when there's no in-app history (e.g. user landed via a deep link). */
  fallback: LinkProps
  /** Label shown when in-app history exists and we'll go back one step. Defaults to "Back". */
  label?: string
  /** Label shown when there's no history and we'll navigate to the fallback. Defaults to `label`. */
  fallbackLabel?: string
  className?: string
  /** Just the arrow (the label becomes its accessible name), for headers that name the parent beside it. */
  iconOnly?: boolean
}

// Goes back via the browser when there's prior in-app history; otherwise
// navigates to `fallback` so deep-link entries still land on a sensible parent.
export function BackButton({
  fallback,
  label = 'Back',
  fallbackLabel,
  className,
  iconOnly = false,
}: BackButtonProps) {
  const canGoBack = useCanGoBack()
  const router = useRouter()

  const classes = cn(
    '-ml-2 h-8 gap-1.5 px-2 text-sm font-medium text-muted-foreground hover:text-foreground',
    iconOnly && 'w-8 px-0',
    className,
  )

  if (canGoBack) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => router.history.back()}
        className={classes}
        aria-label={iconOnly ? label : undefined}
        title={iconOnly ? label : undefined}
      >
        <ArrowLeft className="h-4 w-4" />
        {!iconOnly && label}
      </Button>
    )
  }

  return (
    <Button asChild variant="ghost" size="sm" className={classes}>
      <Link
        {...fallback}
        aria-label={iconOnly ? (fallbackLabel ?? label) : undefined}
        title={iconOnly ? (fallbackLabel ?? label) : undefined}
      >
        <ArrowLeft className="h-4 w-4" />
        {!iconOnly && (fallbackLabel ?? label)}
      </Link>
    </Button>
  )
}
